import { Component, inject, signal, WritableSignal, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { GateCatalogService } from "../../../../../services/api/gate-catalog/gate-catalog.service";

import { GATE_CATALOG_ITEM_TYPES } from "../../../../../enums/gateCatalogItemTypes.enum";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";

import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { CheckboxModule } from "@openng/optimus-ui/checkbox";

import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { first, forkJoin } from "rxjs";
import { InputNumberModule } from "@openng/optimus-ui/inputnumber";
import { TelemetryService } from "../../../../../services/api/telemetry/telemetry.service";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";

import { GateSystem } from "../../../../../models/api/asset/gateSystem";
import { Article } from "../../../../../models/api/gateComponents/article";
import { GateCatalogItem } from "../../../../../models/api/gateComponents/gateCatalogItem";
import { TelemetryResponse } from "../../../../../models/shared/telemetry";
import { DecimalPipe } from "@angular/common";
import { DatePickerModule } from "@openng/optimus-ui/datepicker";
import { SelectChangeEvent, SelectModule } from "@openng/optimus-ui/select";
import { createTimeoutSignal } from "../../../../../shared/utils/timeout.utils";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TimeoutComponent } from "../../../../../shared/components/timeout/timeout.component";

@Component({
  standalone: true,
  selector: "app-gate-component",
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    TranslateModule,
    DatePickerModule,
    DecimalPipe,
    FormsModule,
    CheckboxModule,
    SelectModule,
    ButtonComponent,
    InputNumberModule,
    ProgressSpinnerModule,
    TimeoutComponent,
  ],
  templateUrl: "./gate-component.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./gate-component.component.scss",
})
export class GateComponentComponent implements OnInit {
  public form!: FormGroup;

  private gateCatalogService = inject(GateCatalogService);
  private telemetryService = inject(TelemetryService);
  public translate = inject(TranslateService);

  public loading = signal(true);
  public timeOut = createTimeoutSignal(this.loading);

  public allArticles: WritableSignal<Article[]> = signal([]);
  public selectedArticle: Article | undefined;

  private ref = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig);
  private currentLoadCycle!: number;
  private gateSystem!: GateSystem;

  ngOnInit() {
    if (this.config.data.gateSystem && this.config.data.gateSystem.id) {
      this.gateSystem = this.config.data.gateSystem;
      forkJoin({
        articles: this.getArticles(),
        loadCycle: this.getCurrentLoadCycle(this.config.data.gateSystem.id),
      }).subscribe({
        next: (res: { articles: GateCatalogItem[] | null; loadCycle: TelemetryResponse | null }) => {
          if (res.articles) this.allArticles.set(res.articles as Article[]);
          if (res.loadCycle?.cycle?.[0]?.value) this.currentLoadCycle = res.loadCycle.cycle[0].value;
          this.initForm();
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
    }
  }

  /**
   * Gets the current load cycle for a given gate system.
   * @param gateSystemId The id of the gate system.
   * @returns An observable that emits the current load cycle.
   */
  private getCurrentLoadCycle(gateSystemId: string) {
    return this.telemetryService.getTimeseries(gateSystemId, ASSET_TYPES.GATE_SYSTEM, "cycle").pipe(first());
  }

  /**
   * Updates the form with the selected article's values.
   * @param event The change event from the dropdown.
   */
  public selectArticle(event: SelectChangeEvent) {
    const index = this.allArticles()
      .map((article) => article.id)
      .indexOf(event.value);
    if (index > -1) {
      this.selectedArticle = this.allArticles()[index];
    }
  }

  /**
   * Closes the dialog.
   */
  public closeDialog() {
    this.ref.close();
  }

  /**
   * Closes the dialog with the form data as a result.
   * Converts date to timestamp and article to id.
   * If the gate component is being edited, adds the id to the data.
   */
  public save() {
    const data = this.form.value;
    data.gateSystem = { id: this.gateSystem.id };
    if (data.startDate) {
      data.startDate = data.startDate.getTime();
    }
    if (data.article) {
      data.article = { id: data.article };
    }
    if (this.config.data?.gateComponent?.id) {
      data.id = this.config.data?.gateComponent?.id;
    }
    this.ref.close(data);
  }

  /**
   * Resets the article and its details in the form.
   * Clears the selected article and the form values of the article and its details.
   */
  public clearArticle() {
    this.selectedArticle = undefined;
    this.form.patchValue({
      article: null,
    });
  }

  /**
   * Get all articles from gate catalog.
   * @returns Observable with the article list.
   */
  private getArticles() {
    return this.gateCatalogService.getCatalogItems(GATE_CATALOG_ITEM_TYPES.ARTICLE).pipe(first());
  }

  /**
   * Initializes the form with gate component data.
   * It sets the selected article and its details in the form.
   * If no article is selected, it clears the form values of the article and its details.
   */
  private initForm() {
    const gateComponent = this.config.data?.gateComponent;
    if (gateComponent?.article) {
      this.selectedArticle = this.allArticles().find((article) => article.id === gateComponent.article.id);
    }
    this.form = new FormGroup({
      label: new FormControl(gateComponent?.label ? gateComponent?.label : null, Validators.required),
      article: new FormControl(
        gateComponent?.article?.id ? gateComponent?.article?.id : undefined,
        Validators.required
      ),
      startDate: new FormControl(
        gateComponent?.startDate
          ? new Date(gateComponent?.startDate)
          : new Date(this.gateSystem.activeDate ? this.gateSystem.activeDate : Date.now()),
        Validators.required
      ),
      historyCycle: new FormControl(
        gateComponent?.historyCycle ? gateComponent?.historyCycle : this.currentLoadCycle,

        Validators.required
      ),
    });
  }
}
